import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import FormField from "../../components/forms/FormField/FormField.jsx";
import TextInput from "../../components/forms/TextInput/TextInput.jsx";
import { getStoredUser } from "../../services/api.js";
import { logout } from "../../services/authService.js";
import { changePassword } from "../../services/settingsService.js";
import { useApp } from "../../hooks/useApp.js";
import { TestimonialSettings } from "../../components/dashboard/EngagementSettings/EngagementSettings.jsx";

// Same rule set as the "set a new password" step of ResetPassword.jsx, kept
// in sync deliberately - the backend enforces the same password shape
// (ChangePasswordRequest mirrors ResetPasswordRequest) for both flows.
const passwordChecks = {
  length: (p) => p.length >= 8,
  uppercase: (p) => /[A-Z]/.test(p),
  lowercase: (p) => /[a-z]/.test(p),
  number: (p) => /\d/.test(p),
  special: (p) => /[!@#$%^&*[\]():;\\<>"]/.test(p),
};

const passwordLabels = {
  length: "At least 8 characters",
  uppercase: "One uppercase letter",
  lowercase: "One lowercase letter",
  number: "One number",
  special: "One special character (e.g. !@#$%^&* [ ] ( ) : ; \\ < > \")",
};

const Settings = () => {
  const navigate = useNavigate();
  const { dispatch, showToast } = useApp();
  const user = getStoredUser() || {};

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const passwordResults = Object.keys(passwordChecks).map((key) => ({
    key,
    label: passwordLabels[key],
    valid: passwordChecks[key](newPassword),
  }));
  const passwordValid = passwordResults.every((r) => r.valid);
  const confirmValid = confirmPassword === newPassword && confirmPassword.length > 0;

  // Resets AppContext state (and its sessionStorage-persisted businessId/
  // preparerId/returnId - see AppContext.jsx) so the next sign-in in this
  // tab never inherits this account's business/return.
  const handleSignOut = async () => {
    await logout();
    dispatch({ type: "SEED", which: "new" });
    navigate("/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!currentPassword || !passwordValid || !confirmValid) return;
    setError("");
    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword, confirmPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTouched(false);
      showToast("Password updated");
    } catch (err) {
      setError(err.message || "Couldn't update your password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[700px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="mb-5">
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Account
        </div>
        <h1 className="text-[28px] leading-[1.1] mt-1">Settings</h1>
      </div>

      <Panel padded className="flex flex-col gap-4 mb-5">
        <div className="flex justify-between items-center border-b border-[var(--line)] pb-4">
          <div>
            <div className="text-[13px] text-[var(--muted)] mb-0.5">Email</div>
            <div className="text-[15px] font-semibold text-[var(--ink)]">{user.email || "—"}</div>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div>
            <div className="text-[15px] font-semibold text-[var(--ink)]">Sign out</div>
            <div className="text-[13px] text-[var(--muted)]">End your current session on this device.</div>
          </div>
          <Button variant="ghost" onClick={handleSignOut}>
            Sign out
          </Button>
        </div>
      </Panel>

      <TestimonialSettings />

      <Panel padded>
        <div className="text-[15px] font-semibold text-[var(--ink)] mb-4">Change password</div>
        <form onSubmit={handleChangePassword}>
          <FormField label="Current password" required>
            <TextInput
              type={showPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              invalid={touched && !currentPassword}
              required
            />
          </FormField>

          <FormField label="New password" required>
            <div className="relative">
              <TextInput
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                invalid={touched && !passwordValid}
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </FormField>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1 -mt-2.5 mb-4">
            {passwordResults.map((r) => (
              <div key={r.key} className={["text-xs", r.valid ? "text-[var(--ok)]" : "text-[var(--muted)]"].join(" ")}>
                {r.valid ? "✓" : "•"} {r.label}
              </div>
            ))}
          </div>

          <FormField
            label="Confirm new password"
            required
            error="Passwords do not match."
            showError={touched && !!confirmPassword && !confirmValid}
          >
            <TextInput
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              invalid={touched && !!confirmPassword && !confirmValid}
              required
            />
          </FormField>

          {error && <div className="text-xs text-[var(--danger)] mb-3">{error}</div>}

          <Button type="submit" disabled={submitting}>
            {submitting ? "Updating…" : "Update password"}
          </Button>
        </form>
      </Panel>
    </div>
  );
};

export default Settings;
